import React, { useState, useEffect } from 'react';
import { getInvoices, fetchInvoicePdfUrl } from '../../services/api';
import { PageHeader, StatusBadge } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Download, Eye } from 'lucide-react';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import { useSortableList } from '../../hooks/useSortableList';
import { SortHeader, fmtDate } from '../../components/SortHeader';

const COMPARATORS = {
  invoice:  (i) => i.xeroInvoiceNumber || i.id || '',
  issuedAt: (i) => i.issuedAt || '',
  dueDate:  (i) => i.dueDate || '',
  amount:   (i) => Number(i.amount) || 0,
};

const CustomerInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const pdf = usePdfPreview();
  useEffect(() => { getInvoices().then(setInvoices); }, []);

  const { sorted, sort, onSort } = useSortableList(invoices, COMPARATORS, {
    initialKey: 'issuedAt',
    initialDir: 'desc',
    defaultDescKeys: ['issuedAt', 'dueDate', 'amount'],
  });

  const openPreview = (inv) => pdf.preview(() => fetchInvoicePdfUrl(inv.id));
  const download = (inv) => pdf.download(() => fetchInvoicePdfUrl(inv.id));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader title="Invoices" description="Your billing history and payment status." />
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <SortHeader label="Invoice" sortKey="invoice" sort={sort} onSort={onSort} />
              <SortHeader label="Issued" sortKey="issuedAt" sort={sort} onSort={onSort} />
              <SortHeader label="Due" sortKey="dueDate" sort={sort} onSort={onSort} />
              <SortHeader label="Amount" sortKey="amount" sort={sort} onSort={onSort} />
              <th className="px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 font-medium text-slate-600"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(i => (
              <tr
                key={i.id}
                onClick={() => openPreview(i)}
                className="hover:bg-slate-50 cursor-pointer"
                title="Click to preview invoice"
                data-testid={`customer-invoice-row-${i.id}`}
              >
                <td className="px-4 py-3 font-mono text-xs text-slate-700">
                  <div className="font-semibold text-slate-900">{i.xeroInvoiceNumber || i.id}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(i.issuedAt)}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(i.dueDate)}</td>
                <td className="px-4 py-3 font-semibold text-slate-900">£{Number(i.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openPreview(i)}
                      disabled={pdf.busy}
                      title="Preview PDF"
                      data-testid={`customer-preview-invoice-${i.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => download(i)}
                      title="Download PDF"
                      data-testid={`customer-download-invoice-${i.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Invoice preview"
      />
    </div>
  );
};

export default CustomerInvoices;
